import { useEffect, useRef, useState } from "react";
import { formatVideoTime } from "../hooks/usePitchSubmit";
import { clampToRanges } from "../playback/timeline";

export const clickFraction = (clientX, left, width) => width ? Math.min(1, Math.max(0, (clientX - left) / width)) : 0;

export const Timeline = ({ timeline, played, onSeek, onStep, coarseStepSec = 3, onGoLive, onPointerComplete, sourceVersion, startSecond, endSecond }) => {
  const drag = useRef(null);
  const [preview, setPreview] = useState(null);
  const { kind, ranges } = timeline;
  const start = ranges[0]?.start ?? 0;
  const end = ranges[ranges.length - 1]?.end ?? 0;
  const available = end > start;
  const live = kind === "live" || kind === "youtube-live";
  const cancel = () => {
    const session = drag.current;
    drag.current = null;
    setPreview(null);
    if (session?.element.hasPointerCapture?.(session.pointerId)) session.element.releasePointerCapture(session.pointerId);
  };
  useEffect(() => {
    cancel();
    window.addEventListener("blur", cancel);
    return () => window.removeEventListener("blur", cancel);
  }, [sourceVersion]);
  const position = (seconds) => `${Math.min(100, Math.max(0, (seconds - start) / (end - start) * 100)) || 0}%`;
  const pointerTime = (event, session) => session.start + clickFraction(event.clientX, session.rect.left, session.rect.width) * (session.end - session.start);
  const shown = preview ?? clampToRanges(played, ranges);
  return (
    <section className="video-timeline" aria-label="影片時間軸">
      <div className="timeline-labels">
        <span className="timeline-start">{available ? formatVideoTime(start) : "—"}</span>
        <output className="transport-clock">{preview == null ? "" : "預覽 "}{formatVideoTime(preview ?? played)}</output>
        <span className="timeline-end">{available ? formatVideoTime(end) : "—"}</span>
        {live && <span className="transport-live">● 直播</span>}
        {kind === "live" && <button type="button" className="go-live" disabled={!available} onClick={(e) => { onGoLive?.(); if (e.detail) onPointerComplete?.(); }}>回到直播</button>}
      </div>
      <div className="transport-timeline" role="slider" aria-label="影片進度時間軸（點按跳轉）"
        tabIndex={available ? 0 : -1} aria-disabled={!available}
        aria-valuemin={start} aria-valuemax={end} aria-valuenow={available ? Math.min(end, Math.max(start, shown)) : 0}
        aria-valuetext={formatVideoTime(preview ?? played)}
        style={{ opacity: available ? 1 : 0.5, cursor: available ? "pointer" : "default" }}
        onPointerDown={(e) => {
          if (!available || e.button !== 0 || drag.current) return;
          e.preventDefault(); e.stopPropagation();
          e.currentTarget.focus({ preventScroll: true });
          e.currentTarget.setPointerCapture?.(e.pointerId);
          const session = { element: e.currentTarget, pointerId: e.pointerId, start, end, rect: e.currentTarget.getBoundingClientRect() };
          session.target = pointerTime(e, session);
          drag.current = session;
          setPreview(session.target);
        }}
        onPointerMove={(e) => {
          if (drag.current?.pointerId !== e.pointerId) return;
          drag.current.target = pointerTime(e, drag.current);
          setPreview(drag.current.target);
        }}
        onPointerUp={(e) => {
          if (drag.current?.pointerId !== e.pointerId) return;
          const target = pointerTime(e, drag.current);
          cancel();
          if (!available) { onPointerComplete?.(); return; }
          onSeek(target); // Parent re-reads the latest live window at commit time.
          onPointerComplete?.();
        }}
        onPointerCancel={() => { cancel(); onPointerComplete?.(); }}
        onLostPointerCapture={cancel}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Escape") { e.preventDefault(); cancel(); onPointerComplete?.(); return; }
          if (!available || !["Home", "End", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
          e.preventDefault();
          if (e.key === "Home" || e.key === "End") onSeek(e.key === "Home" ? start : end);
          else onStep(e.key === "ArrowLeft" ? -coarseStepSec : coarseStepSec);
        }}>
        <div className="timeline-track">
          {ranges.map((r) => <span className="timeline-range" key={r.start} style={{ left: position(r.start), width: `${(r.end - r.start) / (end - start) * 100}%` }} />)}
          {available && <span className="timeline-progress" style={{ width: position(shown) }} />}
          {available && startSecond != null && <span className="transport-marker in" style={{ left: position(startSecond) }} />}
          {available && endSecond != null && <span className="transport-marker out" style={{ left: position(endSecond) }} />}
          {available && <span className="transport-playhead" style={{ left: position(shown) }} />}
        </div>
      </div>
      {!available && <span className="timeline-unavailable">{kind === "youtube-live" ? "YouTube 直播無法取得可靠回看範圍，可使用秒數跳轉" : kind === "live" ? "目前沒有可回看的直播範圍" : "載入影片後顯示完整時間軸"}</span>}
    </section>
  );
};
